import { useReducer, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import AggregateMetrics from './AggregateMetrics';
import ItemsChart from './ItemsChart';
import YearlyMetricsChart from './YearlyMetricsChart';
import chartTypes from '../../constants/chartTypes';
import { Message } from '../basics/Message';
import { ANNUAL_MULTI_YEAR, MULTI_YEAR } from '../../constants/years';
import { useFilterContext } from '../../contexts/FilterContext';
import MetricsPieChart from './MetricsPieChart';

const toggleReducer = (state, action) => {
  switch (action.type) {
    case 'toggle': {
      const { accessor, value, allMetrics } = action.payload;
      const toggledUnit = allMetrics.find(
        (metric) => metric.accessor === accessor,
      ).unit;
      return allMetrics.reduce(
        (result, metric) =>
          Object.assign(result, {
            [metric.accessor]:
              metric.accessor === accessor
                ? value
                : // Disable other metrics if the units don't match
                metric.unit !== toggledUnit
                ? false
                : state[metric.accessor],
          }),
        {},
      );
    }
    case 'setAll': {
      return action.payload;
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

const ChartDiv = styled.div`
  border-bottom: 0.5px solid var(--border-color);
  border-left: 0.5px solid var(--border-color);
  width: 100%;
  height: 100%;
  overflow-y: hidden; /* Fix for recharts animation on resize */
  padding: 15px 0;
`;

const SummaryDiv = styled.div`
  display: flex;
  justify-content: space-between;
  max-height: 50vh;
  height: 30vw;
`;

const EMPTY_ARRAY = [];

const MetricsSummary = ({
  // metrics is used in dependencies that update state in a useEffect,
  // so we need a stable reference to avoid an infinte loop
  metrics = EMPTY_ARRAY,
  data = {},
  chartType = chartTypes.YEARLY,
  arrayTransform /* A transform or filter function for the aggregate metrics */,
  msg,
}) => {
  const { year } = useFilterContext().state;
  const initialToggleState = useMemo(
    () =>
      metrics.reduce(
        (s, { accessor, defaultShown }) =>
          Object.assign(s, { [accessor]: !!defaultShown }),
        {},
      ),
    [metrics],
  );

  const [toggleState, dispatchToggle] = useReducer(
    toggleReducer,
    initialToggleState,
  );

  useEffect(() => {
    dispatchToggle({
      type: 'setAll',
      payload: initialToggleState,
    });
  }, [initialToggleState]);

  const metricsWithState = metrics.map((m) => ({
    ...m,
    // Suppress farm level aggregate metrics in single year chart
    shown:
      m.isAggregate && !(year === MULTI_YEAR || year === ANNUAL_MULTI_YEAR)
        ? false
        : toggleState[m.accessor],
  }));

  const onToggleMetric = (accessor) => {
    dispatchToggle({
      type: 'toggle',
      payload: {
        accessor,
        value: !toggleState[accessor],
        allMetrics: metrics,
      },
    });
  };

  useEffect(() => {
    if (!Object.values(chartTypes).includes(chartType)) {
      throw new Error(`Invalid chartType: ${chartType}`);
    }
  }, [chartType]);

  return (
    <SummaryDiv>
      <AggregateMetrics
        metrics={metricsWithState}
        data={data}
        onToggle={onToggleMetric}
        arrayTransform={arrayTransform}
      />
      <ChartDiv>
        {msg ? (
          <Message>{msg}</Message>
        ) : (
          <>
            {chartType === chartTypes.YEARLY && (
              <YearlyMetricsChart data={data} metrics={metricsWithState} />
            )}
            {chartType === chartTypes.ITEMIZED && (
              <ItemsChart data={data} metrics={metricsWithState} />
            )}
            {chartType === chartTypes.SIMPLE_PIE && (
              <MetricsPieChart
                data={[data]}
                metrics={metricsWithState.filter(
                  ({ shown, isAggregate }) => shown && !isAggregate,
                )}
              />
            )}
          </>
        )}
      </ChartDiv>
    </SummaryDiv>
  );
};

export default MetricsSummary;
